export const SUPER_REGION_OPTIONS = [
  { value: "wnam", label: "Western North America" },
  { value: "enam", label: "Eastern North America" },
  { value: "weur", label: "Western Europe" },
  { value: "eeur", label: "Eastern Europe & Middle East" },
  { value: "apac", label: "Asia Pacific" },
  { value: "oc",   label: "Oceania" },
  { value: "latam", label: "Latin America" },
] as const;

export const AWS_GPU_REGIONS = [
  { value: "us-east-1", label: "US East (N. Virginia)", gpu: "G5" },
  { value: "us-west-2", label: "US West (Oregon)", gpu: "G5" },
  { value: "eu-west-2", label: "Europe (London)", gpu: "G5" },
  { value: "eu-west-3", label: "Europe (Paris)", gpu: "G5" },
  { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)", gpu: "G5" },
  { value: "ap-southeast-2", label: "Asia Pacific (Sydney)", gpu: "G5" },
] as const;

// GCP regions available for GPU pool backends.
// The `gpu` field reflects what GCP can ship in that region — L4, T4 vWS,
// and/or RTX PRO 6000 Blackwell (G4 family) — and is a UI hint only, not
// a quota guarantee. Regions marked "no quota allocated yet" will fail to
// provision with a clear "Quota exceeded" error from GCP until someone
// files a quota increase. Africa (africa-south1) and New Zealand are
// intentionally omitted: africa-south1 has zero GPUs of any kind, and
// GCP has no NZ region.
export const GCP_GPU_REGIONS = [
  // North America — fleet (quota allocated)
  { value: "us-west1",             label: "US West (Oregon)",          gpu: "L4/T4/RTX-Pro" },
  { value: "us-west2",             label: "US West (Los Angeles)",     gpu: "T4" },
  { value: "us-central1",          label: "US Central (Iowa)",         gpu: "L4/T4/RTX-Pro" },
  { value: "us-east4",             label: "US East (N. Virginia)",     gpu: "L4/T4/RTX-Pro" },
  // North America — no quota allocated yet
  { value: "us-east1",             label: "US East (S. Carolina)",     gpu: "L4/RTX-Pro" },
  { value: "us-west4",             label: "US West (Las Vegas)",       gpu: "L4/RTX-Pro" },
  // Europe — fleet
  { value: "europe-west1",         label: "Europe West (Belgium)",     gpu: "L4/T4/RTX-Pro" },
  { value: "europe-west2",         label: "Europe West (London)",      gpu: "L4/T4/RTX-Pro" },
  { value: "europe-west3",         label: "Europe West (Frankfurt)",   gpu: "L4/T4" },
  { value: "europe-west4",         label: "Europe West (Netherlands)", gpu: "L4/T4" },
  { value: "europe-west6",         label: "Europe West (Zurich)",      gpu: "L4" },
  // Middle East — no quota allocated yet
  { value: "me-west1",             label: "Middle East (Tel Aviv)",    gpu: "T4" },
  { value: "me-central2",          label: "Middle East (Dammam)",      gpu: "L4" },
  // Asia-Pacific — fleet
  { value: "asia-northeast1",      label: "Asia (Tokyo)",              gpu: "L4/T4" },
  { value: "asia-southeast1",      label: "Asia (Singapore)",          gpu: "L4/T4/RTX-Pro" },
  { value: "asia-east1",           label: "Asia (Taiwan)",             gpu: "L4/T4" },
  { value: "australia-southeast1", label: "Australia (Sydney)",        gpu: "T4" },
  // Asia-Pacific — no quota allocated yet
  { value: "asia-northeast3",      label: "Asia (Seoul)",              gpu: "L4" },
  // South America — no quota allocated yet
  { value: "southamerica-east1",   label: "South America (São Paulo)", gpu: "T4" },
] as const;

export const COREWEAVE_REGIONS = [
  { value: "ORD1", label: "US Central (Chicago)", gpu: "H100/A100/A40" },
  { value: "LGA1", label: "US East (New Jersey)", gpu: "H100/A100/A40" },
  { value: "LAS1", label: "US West (Las Vegas)", gpu: "H100/A100/A40" },
] as const;

// Machine-type dropdowns for the InfrastructureEditor. These are UI
// hints only — any valid string a user enters manually still works via
// the "(custom)" fallback option in the editor. CoreWeave is not
// represented here: its machine spec is expressed via separate per-
// backend cpu/memory/gpu fields on ManagedBackendConfig, not an
// `instanceType` string.
export const GCP_MACHINE_TYPES = [
  // g2 family — built-in L4 GPU (1× L4 per instance)
  { value: "g2-standard-4",  label: "g2-standard-4  (4 vCPU, 16 GB, 1× L4)" },
  { value: "g2-standard-8",  label: "g2-standard-8  (8 vCPU, 32 GB, 1× L4)" },
  { value: "g2-standard-12", label: "g2-standard-12 (12 vCPU, 48 GB, 1× L4)" },
  { value: "g2-standard-16", label: "g2-standard-16 (16 vCPU, 64 GB, 1× L4)" },
  // g4 family — built-in RTX PRO 6000 Blackwell. The fractional sizes
  // (g4-standard-6/12/24, sub-1 GPU) are us-central1-b only; full-GPU
  // sizes are available across the broader G4 region set.
  { value: "g4-standard-48", label: "g4-standard-48 (48 vCPU, 180 GB, 1× RTX-Pro)" },
  { value: "g4-standard-96", label: "g4-standard-96 (96 vCPU, 360 GB, 2× RTX-Pro)" },
  // n1 family — paired with attached nvidia-tesla-t4-vws
  { value: "n1-standard-4",  label: "n1-standard-4  (4 vCPU, 15 GB, + T4 vWS)" },
  { value: "n1-standard-8",  label: "n1-standard-8  (8 vCPU, 30 GB, + T4 vWS)" },
  { value: "n1-standard-16", label: "n1-standard-16 (16 vCPU, 60 GB, + T4 vWS)" },
] as const;

export const AWS_MACHINE_TYPES = [
  // g6 family — NVIDIA L4
  { value: "g6.xlarge",    label: "g6.xlarge    (4 vCPU, 16 GB, 1× L4)" },
  { value: "g6.2xlarge",   label: "g6.2xlarge   (8 vCPU, 32 GB, 1× L4)" },
  { value: "g6.4xlarge",   label: "g6.4xlarge   (16 vCPU, 64 GB, 1× L4)" },
  // g5 family — NVIDIA A10G
  { value: "g5.xlarge",    label: "g5.xlarge    (4 vCPU, 16 GB, 1× A10G)" },
  { value: "g5.2xlarge",   label: "g5.2xlarge   (8 vCPU, 32 GB, 1× A10G)" },
  { value: "g5.4xlarge",   label: "g5.4xlarge   (16 vCPU, 64 GB, 1× A10G)" },
  // g4dn family — NVIDIA T4
  { value: "g4dn.xlarge",  label: "g4dn.xlarge  (4 vCPU, 16 GB, 1× T4)" },
  { value: "g4dn.2xlarge", label: "g4dn.2xlarge (8 vCPU, 32 GB, 1× T4)" },
] as const;

// CoreWeave GPU types. Unlike AWS/GCP where the machine type selects
// both CPU and GPU, CoreWeave's VirtualServer CRD takes a GPU type
// string separately from the CPU/memory counts (which are independent
// per-backend fields: cwCpuCount, cwMemoryGb). The `value` here is
// exactly the string CoreWeave's API expects in `spec.gpu.type`.
export const COREWEAVE_GPU_TYPES = [
  // RTX workstation / A-series — most common for streaming workloads
  { value: "RTX_A4000",        label: "RTX A4000  (16 GB VRAM)" },
  { value: "RTX_A5000",        label: "RTX A5000  (24 GB VRAM)" },
  { value: "RTX_A6000",        label: "RTX A6000  (48 GB VRAM)" },
  // Data center — A series
  { value: "A40",              label: "A40        (48 GB VRAM)" },
  { value: "A100",             label: "A100 PCIe  (40 GB VRAM)" },
  { value: "A100_NVLINK",      label: "A100 NVLINK (40 GB VRAM)" },
  { value: "A100_NVLINK_80GB", label: "A100 NVLINK 80 GB" },
  // Data center — H series (latest generation)
  { value: "H100",             label: "H100 PCIe  (80 GB VRAM)" },
  { value: "H100_NVLINK",      label: "H100 NVLINK (80 GB VRAM)" },
  // Legacy Quadro — kept for backwards compat
  { value: "Quadro_RTX_4000",  label: "Quadro RTX 4000 (8 GB VRAM)" },
  { value: "Quadro_RTX_5000",  label: "Quadro RTX 5000 (16 GB VRAM)" },
] as const;

export const CLOUD_OPTIONS = [
  { value: "aws", label: "Amazon Web Services" },
  { value: "gcp", label: "Google Cloud Platform" },
  { value: "coreweave", label: "CoreWeave" },
] as const;

export const ALLOCATION_STRATEGIES = [
  { value: "balanced", label: "Balanced" },
  { value: "cost-priority", label: "Cost Priority" },
  { value: "latency-priority", label: "Latency Priority" },
] as const;

export type SuperRegion = typeof SUPER_REGION_OPTIONS[number]["value"];
export type CloudProvider = typeof CLOUD_OPTIONS[number]["value"];
export type AllocationStrategy = typeof ALLOCATION_STRATEGIES[number]["value"];
